import { FC, useMemo } from 'react';

import { Icon, Link, RyeconLinkExternal, RyeconPencil } from '@dbt-labs/sourdough';

import { ColumnTable, Entry } from '@/components/ColumnTable';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useExposure } from '@/hooks/dbtStrategy/useExposure';
import { useLinkToDbtCloud } from '@/hooks/links';
import { useCommonLineageEntries } from '@/hooks/useCommonLineageEntries';
import { formatBiProviderName } from '@/util/dbt/exposure';
import { PanelDetails, ResourceDetailsParams } from './panelDetailTypes';
import { TagLinks } from './TagLinks';

export const useExposureDetailsPanel = ({
  uniqueId,
  projectName,
}: ResourceDetailsParams): PanelDetails => {
  const exposureQuery = useExposure(uniqueId);

  const commonEntries = useCommonLineageEntries(projectName);

  return useMemo(() => {
    const exposure = exposureQuery.data.applied;

    if (!exposure) {
      return { ...exposureQuery, rows: null, description: null };
    }

    // use exposure.name instead of uniqueId.remainder
    // to avoid showing deduplication id suffixes of an auto exposure
    // ex. 'customer360_test.MmE1YmUzYjUtNGZjOC00NDc0LWE2ZTktZWIyMDc4YjEyOGJl'
    //     should just be 'customer360_test'
    const nameEntry = commonEntries.find((entry) => entry.key === 'Name');
    if (nameEntry) {
      nameEntry.data = exposure.name!;
    }

    const rows: Entry[] = [
      ...commonEntries,
      { key: 'Exposure Type', data: exposure.exposureType },
      { key: 'Maturity', data: exposure.maturity },
      { key: 'Owner Email', data: exposure.ownerEmail },
      { key: 'Owner Name', data: exposure.ownerName },
      {
        key: 'Tags',
        data: <TagLinks tags={exposure.tags} />,
      },
      { key: 'Package', data: uniqueId.packageName },
    ];

    return { ...exposureQuery, rows, description: exposure.description };
  }, [exposureQuery, commonEntries, uniqueId.packageName]);
};

export const ExposureDetailsTable: FC<ResourceDetailsParams> = ({
  className,
  children,
  compact,
  showAutoExposureActions,
  ...props
}) => {
  const exposureQuery = useExposure(props.uniqueId);
  const exposure = exposureQuery.data?.applied;
  const { accountId, projectId } = useDbtCloudProject();
  const lineageIntegrationSettings = useLinkToDbtCloud('lineageIntegrationSettings');
  const exposureDetails = useExposureDetailsPanel(props);

  return (
    <div className={className}>
      {children?.(exposureDetails)}
      <ColumnTable
        tableEntries={exposureDetails.rows}
        compact={compact}
        isLoading={exposureDetails.fetching}
      />
      <hr />
      {showAutoExposureActions && exposure?.integrationId && (
        <div className="mt-4 space-x-2">
          <Link
            to={exposure?.url || ''}
            isInternal={false}
            buttonStyleType="primary"
            target="_blank"
          >
            <Icon ryecon={RyeconLinkExternal} />
            View in {formatBiProviderName(exposure.autoBiProvider)}
          </Link>
          <Link
            to={
              lineageIntegrationSettings?.({
                accountId,
                projectId,
                lineageIntegrationId: exposure.integrationId,
              }) || ''
            }
            isInternal={false}
            buttonStyleType="secondary"
            target="_blank"
          >
            <Icon ryecon={RyeconPencil} />
            Modify integration
          </Link>
        </div>
      )}
    </div>
  );
};
